/**
 * VideoSection Component
 * Extracted from KnowledgeBasePage.tsx to reduce file size
 */

import React, { useState, useMemo } from 'react';
import type { Video } from '../../types/video';

interface VideoSectionProps {
  videos: Video[];
  loadingVideos: boolean;
  onVideoSelect: (video: Video) => void;
}

const formatDuration = (duration: string): string => {
  // Convert ISO 8601 duration to readable format
  const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
  if (!match) return duration;
  
  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;
  const seconds = match[3] ? parseInt(match[3]) : 0;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Category configuration
const CATEGORY_CONFIG = [
  {
    id: 'recipes',
    name: 'Recipes',
    icon: '🍽️',
    color: '#E07A55',
    keywords: ['recipe', 'recipes', 'cooking', 'cook', 'ingredient', 'ingredients']
  },
  {
    id: 'meal-plan',
    name: 'Meal Planning',
    icon: '📅',
    color: '#D98E73', 
    keywords: ['meal', 'plan', 'planning', 'weekly', 'calendar', 'schedule', 'dinner wizard', 'wizard', 'planner']
  },
  {
    id: 'shopping',
    name: 'Shopping Lists',
    icon: '🛒',
    color: '#6E9C87',
    keywords: ['shopping', 'list', 'lists', 'grocery', 'groceries', 'store']
  }
];

const categorizeVideo = (video: Video): string => {
  const text = `${video.title} ${video.description || ''}`.toLowerCase();
  
  // Check if video has explicit categories
  if (video.category && video.category.length > 0) {
    const category = video.category[0].toLowerCase();
    if (category.includes('recipe')) return 'recipes';
    if (category.includes('meal') || category.includes('plan')) return 'meal-plan';
    if (category.includes('shop') || category.includes('list')) return 'shopping';
  }
  
  // Priority-based keyword matching (most specific phrases first)
  // Check for very specific meal planning terms first
  if (text.includes('dinner wizard') || 
      text.includes('meal plan') || 
      text.includes('plan meals') || 
      text.includes('weekly plan') ||
      text.includes('meal planner') ||
      text.includes('weekly planner')) {
    return 'meal-plan';
  }
  
  // Check for shopping list specific terms
  if (text.includes('shopping list') || 
      text.includes('grocery list') ||
      text.includes('shopping') ||
      text.includes('groceries') ||
      text.includes('grocery store')) {
    return 'shopping';
  }
  
  // Check for recipe specific terms
  if (text.includes('recipe') || 
      text.includes('cooking') || 
      text.includes('ingredient') ||
      text.includes('how to cook') ||
      text.includes('add recipe') ||
      text.includes('create recipe')) {
    return 'recipes';
  }
  
  // Fallback: if it contains general planning terms but not specific meal planning
  if (text.includes('planner') || 
      (text.includes('plan') && text.includes('weekly'))) {
    return 'meal-plan';
  }
  
  return 'recipes'; // Default category
};

export const VideoSection: React.FC<VideoSectionProps> = ({
  videos,
  loadingVideos,
  onVideoSelect
}) => {
  const [activeCategory, setActiveCategory] = useState<string>('recipes');
  
  const categorizedVideos = useMemo(() => {
    const categories: Record<string, Video[]> = {
      'recipes': [],
      'meal-plan': [],
      'shopping': []
    };
    
    videos.forEach(video => {
      const category = categorizeVideo(video);
      categories[category].push(video);
    });
    
    return categories;
  }, [videos]);

  const currentVideos = categorizedVideos[activeCategory] || [];
  
  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold mb-6" style={{ color: '#4A6651' }}>
        Video Tutorials
      </h2>
      
      {loadingVideos ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" 
               style={{ borderColor: '#6E9C87' }}></div>
          <p className="mt-2" style={{ color: '#598D76' }}>Loading videos...</p>
        </div>
      ) : videos.length > 0 ? (
        <>
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {CATEGORY_CONFIG.map((category) => {
              const videoCount = categorizedVideos[category.id]?.length || 0;
              const isActive = activeCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive 
                      ? 'text-white shadow-md' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm'
                  }`}
                  style={isActive ? { backgroundColor: category.color } : {}}
                >
                  <span>{category.icon}</span>
                  <span>{category.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isActive 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {videoCount}
                  </span>
                </button>
              );
            })}
          </div>
          
          {/* Current Category Videos */}
          {currentVideos.length > 0 ? (
            <div className="space-y-4">
              {currentVideos.map((video) => (
                <div
                  key={video.id}
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer flex gap-4 p-4"
                  onClick={() => onVideoSelect(video)}
                >
                  {/* Small Thumbnail */}
                  <div className="relative flex-shrink-0 w-32 h-20 rounded-lg overflow-hidden bg-gray-200">
                    <img
                      src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        // Try different quality thumbnails in order
                        if (img.src.includes('mqdefault')) {
                          img.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                        } else if (img.src.includes('hqdefault')) {
                          img.src = `https://img.youtube.com/vi/${video.youtube_id}/sddefault.jpg`;
                        } else if (img.src.includes('sddefault')) {
                          img.src = `https://img.youtube.com/vi/${video.youtube_id}/default.jpg`;
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    {video.duration && (
                      <span className="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white text-xs px-1.5 py-0.5 rounded text-[10px]">
                        {formatDuration(video.duration)}
                      </span>
                    )}
                  </div>
                  
                  {/* Video Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg mb-1 line-clamp-2" style={{ color: '#4A6651' }}>
                      {video.title}
                    </h3>
                    {video.description && (
                      <p className="text-sm line-clamp-2 mb-2" style={{ color: '#598D76' }}>
                        {video.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      {video.view_count !== undefined && (
                        <p className="text-xs" style={{ color: '#8B9A91' }}>
                          {video.view_count.toLocaleString()} views
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-white rounded-xl">
              <div className="mb-4">
                <span className="text-4xl">
                  {CATEGORY_CONFIG.find(cat => cat.id === activeCategory)?.icon}
                </span>
              </div>
              <p style={{ color: '#598D76' }}>
                No videos available in this category yet.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 bg-white rounded-xl">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="#D9C88E" className="mx-auto mb-4">
            <path d="M21 3H3c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.11-.9-2-2-2zm0 14H3V5h18v12zm-5-9l-7 4V8l7 4z"/>
          </svg>
          <p style={{ color: '#598D76' }}>No videos available yet.</p>
        </div>
      )}
    </div>
  );
};